import {
  API_URL,
  dropDynamicTranslations,
  REFRESH_TIME,
  setShop,
  setToken,
  THEMES,
} from '../utils/helpers';
import { JwtToken } from '../utils/types';
import { VerifiedShop } from './orderLookupService';
import { resolveEntryLanguage } from '../i18n/languages';

// #72: the session of a parcel QR opened full screen. It lives in this tab's
// memory only: nothing is read from the shared storage (another tab may hold
// another merchant there) and nothing is written to it. Each install starts a
// new generation; a refresh answer of an older generation is dropped.

const DEFAULT_THEME = 'version2';

// theme = the URL `theme` when it is a known one, else the default
export const standaloneTheme = (search: string): string => {
  const theme = new URLSearchParams(search).get('theme');
  return THEMES.some(t => t.name === theme) ? (theme as string) : DEFAULT_THEME;
};

// language = the URL `default-language`, else the first supported browser
// language, else English (#95: the registry's normalisation)
export const standaloneLanguage = (
  search: string,
  browserLanguages: readonly string[]
): string => resolveEntryLanguage(search, browserLanguages);

let generation = 0;
let sessionToken = '';
let sessionShopId = 0;
// #154 (Codex r1 #2): the order the redeemed session was issued for (0 =
// not bound); set by the single entry's dispatcher after the redemption
let sessionOrderId = 0;
let refreshTimer: ReturnType<typeof setInterval> | null = null;

const stopRefresh = () => {
  if (refreshTimer !== null) {
    clearInterval(refreshTimer);
    refreshTimer = null;
  }
};

const refreshSession = async (sessionGeneration: number): Promise<void> => {
  try {
    const response = await fetch(API_URL + '/refresh-token', {
      method: 'POST',
      headers: { Authorization: 'Bearer ' + sessionToken },
    });
    if (!response.ok) return;
    const refreshed: JwtToken = await response.json();
    // another session was installed (or this one ended) meanwhile, or the
    // answer is for another shop: never overwrite the current session
    if (sessionGeneration !== generation || refreshed.shop !== sessionShopId) {
      return;
    }
    sessionToken = refreshed.access_token;
    setToken(sessionToken);
  } catch (error) {
    console.error('Error refreshing token:', error);
  }
};

// One step: shop, client token and labels of the verified shop, then the
// refresh bound to this session.
export const installStandaloneSession = (
  shop: VerifiedShop,
  token: string
): void => {
  stopRefresh();
  const installed = ++generation;
  sessionToken = token;
  sessionShopId = shop.id;
  sessionOrderId = 0;
  setShop(shop.id);
  setToken(token);
  dropDynamicTranslations();
  refreshTimer = setInterval(
    () => refreshSession(installed),
    REFRESH_TIME * 60 * 1000
  );
};

// No shop, no token, no labels, no refresh (pending and failed entries).
export const resetStandaloneSession = (): void => {
  stopRefresh();
  ++generation;
  sessionToken = '';
  sessionShopId = 0;
  sessionOrderId = 0;
  setShop(0);
  setToken('');
  dropDynamicTranslations();
};

// The flow reached the signed status page (no client token needed there):
// the refresh stops and the client token is dropped; the shop and its labels
// stay for the page on screen. Entries of this session then fail closed.
export const endStandaloneSession = (): void => {
  stopRefresh();
  ++generation;
  sessionToken = '';
  sessionShopId = 0;
  sessionOrderId = 0;
  setToken('');
};

// #149: the credentials of the redeemed session (read at request time by the
// withdrawal form and receipt), null when none
export const standaloneCredentials = (): {
  token: string;
  shopId: number;
} | null =>
  sessionShopId > 0 && sessionToken !== ''
    ? { token: sessionToken, shopId: sessionShopId }
    : null;

// #154: binds the redeemed session to its order (the dispatcher, right after
// `succeed`); a new, reset or ended session drops it
export const bindStandaloneOrder = (orderId: number): void => {
  sessionOrderId = orderId;
};

export const standaloneOrderId = (): number | null =>
  sessionOrderId > 0 ? sessionOrderId : null;

// Whether this tab holds a redeemed session (never true after a reload).
export const hasStandaloneSession = (): boolean => sessionShopId > 0;

// Refreshes the current session now (the timer does the same).
export const refreshStandaloneSessionNow = (): Promise<void> =>
  refreshSession(generation);
